const TiledImageLayer = require("./layers/tiledimagelayer");
const TiledDataOverlay = require("./layers/tileddataoverlay");
const BaseView = require("./baseview").BaseView;
const getProjectConfig = require("./datasource").getProjectConfig;
const SearchParameters = require("./searchparameters").SearchParameters;
const maps = require("./maps");

/**
 * TiledViewOptions
 * @typedef {Object} TiledViewOptions
 * @extends {BaseViewOptions}
 * @property {Boolean} [paddedStoreCenter] sets the paddedStoreCenter
 * @property {Style} [style] the style for the tiled vector layer.
 * @property {Number} [breakPoint] the zoom level to change the representation.
 * @property {TileStyle} [tileStyle] the style for the tiled image layer.
 * @property {woosmap.search.SearchQuery} [searchQuery] Deprecated - use `searchParameters`.
 * a searchQuery to filter stores.
 * @property {woosmap.search.SearchParameters} [searchParameters]
 * @property {PolylineFilter} [polylineFilter] a polylineFilter to filter stores.
 * @property {woosmap.layers.renderers.DefaultRenderer}
 * [interactionRenderer] the renderer used to display search results.
 * @property {woosmap.layers.renderers.DefaultRenderer} [tiledDataRenderer] the renderer used to display vector tiles.
 * @property {Boolean} [gentleCenter] when gentleCenter is true the view will center on the selectedStore only if it's
 * not already in the padded zone.
 */

/**
 * TiledView this view is mostly compatible with {woosmap.View}.
 * This object mixes two layers:
 *  - a tiled image layer for higher zoom levels
 *  - a tiled vector layer for lower zoom levels
 * @class
 * @extends woosmap.BaseView
 * @memberOf woosmap
 * @since 1.1.0
 */
class TiledView extends BaseView {
    /**
     * @param {google.maps.Map} map
     * @param {TiledViewOptions} [options]
     * @constructor
     */
    constructor(map, options) {
        options = options || {};
        super(arguments);
        super.init(map, options);
        this.enablePaddedStoreCenter(options.gentleCenter || false);

        this.selectedStore = null;
        this._map = map;
        this._searchParameters =
            options.searchParameters ||
            new SearchParameters({
                querySearchOptions: options.searchQuery,
                polylineFilter: options.polylineFilter,
            });
        this._style = this.options.style;
        this._breakPoint = this.options.breakPoint || 7;

        this._selectedStoreMarker = maps.utils.getMarker();
        this._zoomChangedListener = maps.events.addListener(
            this._map,
            "zoom_changed",
            this._updateSelectedStoreMarkerVisibility.bind(this),
        );

        this._tiledDataOverlay = new TiledDataOverlay({
            eventManager: this._storeEventManager,
            minZoom: this._breakPoint + 1,
            styler: this._styler,
            renderer: this.options.tiledDataRenderer,
            searchParameters: this._searchParameters,
        });

        //this._tiledDataOverlay.setMap(this._map);

        this._tiledImageLayer = null;

        getProjectConfig(
            function (config) {
                this._tiledImageLayer = new TiledImageLayer({
                    updatedAt: config.updated,
                    maxZoom: this._breakPoint,
                    eventManager: this._storeEventManager,
                    style: options.tileStyle || {},
                    searchParameters: this._searchParameters,
                });

                if (!this.get("stores") || (this.get("stores") && this.get("stores").length === 0)) {
                    //this._tiledImageLayer.setMap(this._map);
                }
            }.bind(this),
        );
    }

    /**
     * Sets the searchParameters of tiledDataOverlay and tiledImageLayer.
     * @private
     */
    _refreshLayersSearchParameters() {
        this._tiledDataOverlay.setSearchParameters(this._searchParameters);
        if (this._tiledImageLayer) {
            this._tiledImageLayer.setSearchParameters(this._searchParameters);
        }
    }

    /**
     * Sets the `searchParameters` used to filter both image and vector layer.
     * @param {woosmap.search.SearchParameters} searchParameters
     */
    setSearchParameters(searchParameters) {
        this._searchParameters = searchParameters;
        this._refreshLayersSearchParameters();
    }

    /**
     * Sets the `searchQuery` used to filter both image and vector layer.
     * @param {woosmap.search.SearchQuery} searchQuery
     * @deprecated
     */
    setSearchQuery(searchQuery) {
        this._searchParameters.searchQuery = searchQuery;
        this._refreshLayersSearchParameters();
    }

    /**
     * Sets the `polylineFilter` used to filter both image and vector layer.
     * @param {PolylineFilter}polylineFilter
     * @deprecated
     */
    setPolylineFilter(polylineFilter) {
        this._searchParameters.polylineFilter = polylineFilter;
        this._refreshLayersSearchParameters();
    }

    stores_changed() {
        const stores = this.get("stores");
        if (stores && stores.length > 0) {
            if (this._tiledImageLayer) {
                this._tiledImageLayer.setMap(null);
            }
            this._tiledDataOverlay.setMap(null);
        } else {
            if (this._tiledImageLayer) {
                this._tiledImageLayer.setMap(this._map);
            }
            this._tiledDataOverlay.setMap(this._map);
            this.removeAllStores();
            this._updateSelectedStoreMarkerVisibility();
        }
        super.stores_changed();
    }

    selectedStore_changed() {
        const store = this.get("selectedStore");
        this._tiledDataOverlay.selectStore(store);
        super.selectedStore_changed();

        if (store) {
            maps.utils.setMarkerOptions(this._selectedStoreMarker, {
                icon: this._styler.getMarkerStyleForStore(store, { selected: true }),
                position: { lat: store.geometry.coordinates[1], lng: store.geometry.coordinates[0] },
            });
        } else {
            maps.utils.setMarkerOptions(this._selectedStoreMarker, {
                position: null,
                icon: null,
            });
        }
        this._updateSelectedStoreMarkerVisibility();
    }

    _updateSelectedStoreMarkerVisibility() {
        if (this.get("selectedStore") == null || this._map.getZoom() > this._breakPoint) {
            maps.utils.setMap(this._selectedStoreMarker, null);
        } else {
            maps.utils.setMap(this._selectedStoreMarker, this._map);
        }
    }
}

module.exports = TiledView;
